import React from 'react';
import { Box, PagePanel } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { LUNCH } from '@epi-constants/benefitTypes';
import BenefitSettingsLunch from '@epi-containers/ManageBenefits/components/BenefitSettingsModal/BenefitSettingsLunch';
import BenefitSettingsItem from '@epi-containers/ManageBenefits/components/BenefitSettingsModal/BenefitSettingsItem';
import { IAvailableBenefits } from '@epi-models/containers/ManageBenefits';
import { useBenefitAmounts } from '@epi-containers/ManageBenefitGroups/hooks';
import { goToPage } from '@epi-actions/navigation';
import { routerPaths } from '@epi-constants/routerPaths';
import { OnboardingSupportInformation } from '@epi-components/OnboardingSupportInformation';

interface ActivatedBenefitsListProps {
  selectedBenefits: IAvailableBenefits[];
}

export const ActivatedBenefitsList = ({
  selectedBenefits
}: ActivatedBenefitsListProps) => {
  const { t } = useTranslation();
  const {
    sizeExtraExtraLarge,
    sizeMedium,
    gray50,
    gray85,
    fontRegularBold,
    fontSemiBold
  } = useTheme();
  const dispatch = useDispatch();
  const benefitMaxAmounts = useBenefitAmounts();
  const {
    formState: { isValid }
  } = useFormContext();

  const handleGoBack = () => {
    dispatch(goToPage(routerPaths.manageBenefits));
  };

  return (
    <Box>
      <PagePanel
        footer={{
          submitText: t('components.manage_benefits.activate_benefits'),
          submitButtonWidth: '240px',
          disableSubmit: !isValid,
          backText: t('components.onboarding.buttons.back'),
          onBack: handleGoBack
        }}
      >
        <Box
          fontSize={sizeExtraExtraLarge}
          fontWeight={fontSemiBold}
          color={gray85}
        >
          {t('components.manage_benefit_modal.title')}
        </Box>
        <Box
          mt={1}
          fontSize={sizeMedium}
          fontWeight={fontRegularBold}
          color={gray50}
        >
          {t('components.manage_benefit_modal.subtitle')}
        </Box>
        <Box display="flex" flexDirection="column" gap={4}>
          {selectedBenefits.some(s => s.type === LUNCH) && (
            <BenefitSettingsLunch
              maxAmounts={benefitMaxAmounts[LUNCH]}
              index={selectedBenefits.findIndex(s => s.type === LUNCH)}
            />
          )}
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(420px, 1fr))"
            gap={3}
          >
            {selectedBenefits
              .filter(benefit => benefit.type !== LUNCH)
              .map(benefit => {
                return (
                  <BenefitSettingsItem
                    benefit={benefit}
                    maxAmounts={benefitMaxAmounts[benefit.type]}
                    key={benefit.id}
                    index={selectedBenefits.findIndex(
                      s => s.type === benefit.type
                    )}
                  />
                );
              })}
          </Box>
        </Box>
      </PagePanel>
      <OnboardingSupportInformation />
    </Box>
  );
};
